import { useState, useCallback } from 'react'
import { FormattingResponse, FormattingHistory, UsageInfo } from '../types/formatting'
import { useAuth } from '../auth/AuthContext'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

interface UseDocumentFormattingResult {
  formatDocument: (documentId: string, options?: { forceRegenerate?: boolean }) => Promise<FormattingResponse>
  getFormattingHistory: (documentId: string) => Promise<FormattingHistory>
  revertToVersion: (documentId: string, versionId: string) => Promise<{ success: boolean }>
  isLoading: boolean
  error: string | null
  usageInfo: UsageInfo | null
}

export function useDocumentFormatting(): UseDocumentFormattingResult {
  const { firebaseUser } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [usageInfo, setUsageInfo] = useState<UsageInfo | null>(null)

  const formatDocument = useCallback(async (
    documentId: string,
    options: { forceRegenerate?: boolean } = {}
  ): Promise<FormattingResponse> => {
    if (!firebaseUser) {
      throw new Error('ユーザー認証が必要です')
    }

    try {
      setIsLoading(true)
      setError(null)

      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId

      const response = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${documentId}/format`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          forceRegenerate: options.forceRegenerate || false
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        let errorMessage = errorData.error || 'フォーマット処理に失敗しました'

        // Rate limiting specific handling
        if (response.status === 429) {
          errorMessage = 'AI サービスが混雑しています。しばらく待ってから再試行してください。'
        } else if (response.status === 503) {
          errorMessage = 'AI サービスが一時的に利用できません。後ほど再試行してください。'
        }

        throw new Error(errorMessage)
      }

      const result: FormattingResponse = await response.json()

      // 使用量情報を更新
      setUsageInfo({
        currentCount: result.usageInfo.currentCount,
        limitReached: result.usageInfo.limitReached,
        remainingCount: result.usageInfo.remainingCount,
        monthlyLimit: result.usageInfo.currentCount + result.usageInfo.remainingCount
      })

      return result

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'フォーマット処理に失敗しました'
      setError(errorMessage)
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [firebaseUser])

  const getFormattingHistory = useCallback(async (documentId: string): Promise<FormattingHistory> => {
    if (!firebaseUser) {
      throw new Error('ユーザー認証が必要です')
    }

    try {
      setIsLoading(true)
      setError(null)

      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId

      const response = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${documentId}/formatting/history`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || '履歴の取得に失敗しました')
      }

      const history: FormattingHistory = await response.json()

      // 日付をDateオブジェクトに変換
      history.versions = history.versions.map(version => ({
        ...version,
        createdAt: new Date(version.createdAt)
      }))

      return history

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '履歴の取得に失敗しました'
      setError(errorMessage)
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [firebaseUser])

  const revertToVersion = useCallback(async (
    documentId: string,
    versionId: string
  ): Promise<{ success: boolean }> => {
    if (!firebaseUser) {
      throw new Error('ユーザー認証が必要です')
    }

    try {
      setIsLoading(true)
      setError(null)

      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId

      const response = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${documentId}/formatting/revert/${versionId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'バージョンの復元に失敗しました')
      }

      return { success: true }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'バージョンの復元に失敗しました'
      setError(errorMessage)
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [firebaseUser])

  return {
    formatDocument,
    getFormattingHistory,
    revertToVersion,
    isLoading,
    error,
    usageInfo
  }
}